/* eslint-disable no-mixed-operators */
/**
 * This component renders the GreaseCloud Side Navigation Bar. Styles for this component are specifically
 * defined in NavBar.module.scss in the Shared/Styles folder.
 *
 */
import { CSSProperties, MouseEvent as ReactMouseEvent, ReactElement, useEffect, useState } from "react";
import useIdentityStore from "../../../Zustand/identityStore";
import { useNavigate, useLocation } from "react-router-dom";
import useLayoutStore from "../../../Zustand/layoutStore";
import { Transition } from "react-transition-group";
import GenericConfirmationDialog from "../../Shared/Components/Dialogs/GenericConfirmationDialog";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../messages/en-US";
import { GetSideBarPanelItems } from "./SideBarPanelItems";
import { distinct } from "@progress/kendo-data-query";
import { ClientRoutes } from "../../../Constants/ClientRoutingConstants";
import { PanelBar, PanelBarSelectEventArguments, PanelBarUtils } from "@progress/kendo-react-layout";
import { useAccountSettingsStore } from "../../../Zustand/accountSettingsStore";
import useAssetStore from '../../../Zustand/hmeDashboardStore';



//Divider function that renders the divider line between sections as neccessary.
export default function SideBar(): ReactElement {
  const localizationService = useLocalization();
  const isOpen = useLayoutStore((x) => x.isSidebarOpen);
  const isModifiedData = useLayoutStore((x) => x.isModifiedData);
  const setModifiedData = useLayoutStore((x) => x.setModifiedDataCounts);
  const user = useIdentityStore((store) => store.user);
  const softwareFeatures = useAccountSettingsStore((state) => state.softwareFeatures);
  const [expanded, setExpanded] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const [confirmAccessPage, setConfirmAccessPage] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>();
  var [panelItems, setPanelItems] = useState<any[]>([]);
  const setSelectedAsset = useAssetStore(state => state.setSelectedAsset);

  useEffect(() => {
    const panelItems = GetSideBarPanelItems(localizationService);
    var panelItemsForUserRole: any[] = [];

    if (user?.profile.role !== undefined && softwareFeatures !== null) {
      for (let i = 0; i < panelItems.length; i++) {
        var panelItem = {...panelItems[i]};

        // Check if the item requires a feature and if that feature is enabled
        if (panelItem.requiresFeature && !softwareFeatures[panelItem.requiresFeature as keyof typeof softwareFeatures]) {
          continue; // Skip this item if the required feature is not enabled
        }

        if (panelItem.role.length === 0 || panelItem.role.includes(user?.profile.role)) {
          if (panelItem.svgIcon) {
            // Just pass the name property
            panelItem.icon = panelItem.svgIcon.name;
          }

          var children: any[] = [];
          if (panelItem.children !== undefined) {
            for (let j = 0; j < panelItem.children.length; j++) {
              // Also check feature requirements for child items
              if (panelItem.children[j].requiresFeature && 
                  !softwareFeatures[panelItem.children[j].requiresFeature as keyof typeof softwareFeatures]) {
                continue; // Skip this child if the required feature is not enabled
              }

              if (
                panelItem.children[j].role.length === 0 ||
                panelItem.children[j].role.includes(user?.profile.role) ||
                panelItem.children[j].bypassRoleForSiteIds?.includes(user?.profile?.current_site_id)
              ) {
                if (panelItem.children[j].svgIcon) {
                  // Same for children
                  panelItem.children[j].icon = panelItem.children[j].svgIcon.name;
                }
                // Filter sub-children by role (e.g. Site > Templates)
                var childItem = {...panelItem.children[j]};
                if (childItem.children !== undefined) {
                  childItem.children = childItem.children.filter(
                    (sub: any) => sub.role.length === 0 || sub.role.includes(user?.profile.role)
                  );
                }
                children.push(childItem);
              }
            }
            panelItem.children = children;
          }
          panelItemsForUserRole.push(panelItem);
        }
      }
    }

    // Wrap navigable items in a real anchor (enables open-in-new-tab). Kendo renders
    // the label from `title`, so the link goes there.
    const buildTitle = (item: any) =>
      item.route ? (
        <a href={item.route} onClick={handleAnchorClick} className="sidebar-panelbar-link">
          {item.title}
        </a>
      ) : (
        item.title
      );

    // Recurse so all levels (incl. 3rd-level items) become links.
    const mapItem = (item: any): any => ({
      ...item,
      title: buildTitle(item),
      children: item.children?.map(mapItem)
    });

    const mappedItems = panelItemsForUserRole.map(mapItem);

    setPanelItems(PanelBarUtils.mapItemsToComponents(mappedItems));
  }, [user?.profile.role, localizationService, softwareFeatures]);


  //Select the correct panel item when navigating to a page, for notifications page select nothing
  useEffect(() => {

    var route = location.pathname + location.hash;

    // Site explorer route can contain additional parameters, but we only need to check the base route
    if (route.includes("siteexplorer")) {
      route = ClientRoutes.SiteExplorerPage;
    }

    if (route.includes("notifications")) {
      setSelected("");
    } else {
      var selectedId = ""
      var expandedId: string[] = [...expanded];

      // Try to find the route in the top level panel items
      var child = panelItems.find((x: any) => x.props.route.includes(route));

      if (child !== undefined) {
        selectedId = child.props.id;
      } else {
        // Route was not found in the top level panel items, try to find it in the children
        for (let i = 0; i < panelItems.length && selectedId === ""; i++) {
      
          if (panelItems[i].props.children !== undefined) {
            child = panelItems[i].props.children.find((x: any) => x.props.route.includes(route));

            if (child !== undefined) {
              selectedId = child.props.id;
              expandedId.push(panelItems[i].props.id);
            } else {
              // Route was not found in the children, try to find it in the sub children
              for (let j = 0; j < panelItems[i].props.children.length && selectedId === ""; j++) {
                if (panelItems[i].props.children[j].props.children !== undefined) 
                {
                  var subChild = panelItems[i].props.children[j].props.children.find((x: any) => x.props.route.includes(route));

                  if (subChild !== undefined) {
                    selectedId = subChild.props.id;
                    expandedId.push(panelItems[i].props.id);
                    expandedId.push(panelItems[i].props.children[j].props.id);
                  }
                }
              }
            }
          }
        }
      }

      if (selectedId !== "")
      {
        setSelected(selectedId);
        setExpanded(distinct(expandedId));
        setModifiedData({});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, panelItems]);

  

  // New-tab gestures: let the browser handle it (stop Kendo's onSelect). Plain click: in-app nav.
  const handleAnchorClick = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
  };

  const handlePanelSelect = (e: PanelBarSelectEventArguments) => {
    // If user is trying to leave page, show confirmation modal
    if (isModifiedData() && !e.target.key!.toString().includes("Heading")) {
      setConfirmAccessPage(e.target.props.route);
    } else {
      if (e.target.props.route !== "") {
        setModifiedData({});
        // Always force navigation to the dashboard for the Assets menu item
        if (e.target.props.route === ClientRoutes.AssetsDashboardPage) {
          setSelectedAsset(null);
          navigate(ClientRoutes.AssetsDashboardPage, { replace: true });
        } else {
          navigate(e.target.props.route);
        }
      }
    }

    if (e.expandedItems) {
      setExpanded(e.expandedItems);
    }
  };

  const defaultStyle: CSSProperties = {
    transition: `transform 200ms ease`,
    transform: "translateX(0%)",
  };

  const transitionStyles = {
    entering: { transform: "translateX(0%)" } as CSSProperties,
    entered: { transform: "translateX(0%)" } as CSSProperties,
    exiting: { transform: "translateX(-100%)" } as CSSProperties,
    exited: { transform: "translateX(-100%)" } as CSSProperties,
  };


  /**
   * RENDER
   */
  return (
    
    <>
      <Transition in={isOpen} timeout={10}>
        {(state) => (
          <div
            className={`border-r-2 z-20 bg-white absolute h-full xl:h-auto xl:sticky`}
            style={{
              ...defaultStyle,
              ...transitionStyles[state],
              minWidth: "230px",
            }}
          >
            <PanelBar
              className="border-0 border-collapse"
              style={{ 
                position: "sticky", 
                top: "68px",
                maxHeight: "calc(100vh - 68px)", // Subtract header height
                overflowY: "auto",
                overflowX: "hidden"
              }}
              isControlled={true}
              onSelect={handlePanelSelect}
              expanded={expanded}
              selected={selected}
              children={panelItems}
              animation={true}
            />
          </div>
        )}
      </Transition>
      {/* Are you sure you want to leave modal */}
      <GenericConfirmationDialog open={confirmAccessPage != null} onClose={function (): void { setConfirmAccessPage(null);}}
        onConfirm={function (): void {navigate(confirmAccessPage!);}}
        header={localizationService.toLanguageString("custom.confirmLeave",enMessages.custom.confirmLeave)}
        body={localizationService.toLanguageString("custom.unsavedChanges",enMessages.custom.unsavedChanges)}
        confirmButtonTheme="primary"
        cancel={localizationService.toLanguageString("custom.cancel", enMessages.custom.cancel)}
        confirm={localizationService.toLanguageString("custom.confirm",enMessages.custom.confirm
        )}
      />
    </>
  );
}

